import { useRouter } from 'next/router'
import useTranslation from 'next-translate/useTranslation'
import { useMemo } from 'react'

import { DistanceUnit, Place, PlaceType } from '@unreserved-frontend-v2/api/generated/graphql/types'
import { stringAddressToObject } from '@unreserved-frontend-v2/geolocation/components/geosearch-filter/utils'
import { GeoInputForUI } from '@unreserved-frontend-v2/geolocation/types'
import { ComparablesInfoBoxProps } from '@unreserved-frontend-v2/modules/listings/components/details/comparables-info/comparables-info'
import { buildQueryStringFromFilters } from '@unreserved-frontend-v2/modules/listings/utils/routing/build-query-string-from-filters'
import { DEFAULT_LISTING_DIRECTORY } from '@unreserved-frontend-v2/modules/listings/utils/routing/constants'
import { BreadcrumbItem } from '@unreserved-frontend-v2/ui/SEO-header/types'
import { currency } from '@unreserved-frontend-v2/utils/currency'
import { ORIGIN_HOST } from '@unreserved-frontend-v2/utils/local-storage/constants'
import { DEFAULT_LOCALE } from '@unreserved-frontend-v2/utils/locale'

export const useSEOHeaderValues = (
  totalResults: number,
  filterSelections: Record<string, unknown>,
  place?: Partial<Place> | null,
  comparablesData?: ComparablesInfoBoxProps['data']
) => {
  const { t } = useTranslation()
  const { query, pathname } = useRouter()
  const { residentialTypes, listingStatuses } = filterSelections
  const selectedListingType = useMemo(() => ([listingStatuses].flat()[0] as string)?.toLowerCase(), [listingStatuses])
  const selectedResidentialType = useMemo(() => {
    const type = ([residentialTypes].flat()[0] as string)?.toLowerCase()
    return type === 'all' ? null : type
  }, [residentialTypes])

  const translatedSegments = useMemo(() => {
    /**
     * Mapping residentialtypes and listingStatuses to their translated equivalents if applicable
     *
     * (e.g. `residentialTypes: [Real Estate/Houses/Condos] listingStatuses:[For Sale/Sold]`
     */
    return {
      residentialTypes: t(
        `listings:SEO-header.${(query.listing ? selectedResidentialType : null) || 'real-estate'}`,
        { count: 0 },
        { fallback: t('listings:SEO-header.real-estate') }
      ),
      listingStatuses: t(`listings:SEO-header.${selectedListingType}`, undefined, {
        fallback: t('listings:SEO-header.active'),
      }),
    }
  }, [query.listing, selectedListingType, selectedResidentialType, t])

  // Setting up breadcrumb items
  const breadcrumbItems: BreadcrumbItem[] = useMemo(() => {
    // Returning slugged breadcrumbs
    return [
      {
        nameShort: `${translatedSegments.residentialTypes} ${translatedSegments.listingStatuses}`,
        id: 'section',
        slug: null,
      },
      ...([place?.country, place?.region, place?.metroArea, place?.city, place].filter(Boolean) as Partial<Place>[]),
    ].map<BreadcrumbItem>(({ slug, nameShort, id }) => ({
      label: nameShort,
      id,
      // Overwriting with usable formatted href slug
      url:
        id !== 'section'
          ? buildQueryStringFromFilters(
              { residentialTypes, listingStatuses, placeSlugs: slug ? [slug] : undefined },
              pathname,
              !query.listing
            )
          : undefined,
    }))
  }, [
    translatedSegments.residentialTypes,
    translatedSegments.listingStatuses,
    place,
    residentialTypes,
    listingStatuses,
    pathname,
    query.listing,
  ])

  const count = useMemo(() => query.page || 0, [query])

  // Formatting breadcrumbs title
  const title = useMemo(() => {
    const geo = filterSelections.geo as GeoInputForUI
    const isStreet = place?.type === PlaceType.Street

    // Display different h1 title if an address (not placeSlug) is selected
    if (geo?.distance && geo?.displayAddress) {
      return t('listings:SEO-header.residential-listing-within-distance-of-address', {
        ...translatedSegments,
        distance: `${geo.distance.value} ${geo.distance.unit === DistanceUnit.Kilometers ? 'km' : 'm'}`,
        address:
          geo.displayAddress === t('geolocation:location-selector.current-location')
            ? geo.displayAddress.toLocaleLowerCase()
            : stringAddressToObject(geo.displayAddress)?.address1,
        count,
      })
    }

    return t(`listings:SEO-header.residential-listing-${isStreet ? 'on' : 'in'}-place`, {
      ...translatedSegments,
      placeName:
        (isStreet
          ? [place?.city?.nameShort, place?.region?.nameAbbreviated].filter(Boolean).join(', ')
          : place?.nameLong) || t('listings:SEO-header.canada'),
      streetName: isStreet ? place.nameShort : '',
      count,
    })
  }, [
    filterSelections.geo,
    place?.city?.nameShort,
    place?.nameLong,
    place?.nameShort,
    place?.region?.nameAbbreviated,
    place?.type,
    t,
    translatedSegments,
    count,
  ])

  // Formatting the SEO description
  const metaDescription = useMemo(() => {
    const geo = filterSelections.geo as GeoInputForUI
    const sharedVars = {
      totalResults: totalResults.toLocaleString(DEFAULT_LOCALE),
      residentialTypes: t(
        `listings:SEO-header.${selectedResidentialType || 'homes'}`,
        { count: totalResults },
        { fallback: t('listings:SEO-header.homes', { count: totalResults }) }
      ).toLowerCase(),
    }

    // Display different description if an address (not placeSlug) is selected
    if (geo?.distance && geo?.displayAddress) {
      return t(`listings:SEO-header.meta-${selectedListingType || 'active'}-description-within`, {
        ...sharedVars,
        distance: `${geo.distance.value} ${geo.distance.unit === DistanceUnit.Kilometers ? 'km' : 'm'}`,
        address:
          geo.displayAddress === t('geolocation:location-selector.current-location')
            ? geo.displayAddress.toLocaleLowerCase()
            : stringAddressToObject(geo.displayAddress)?.address1,
        count,
      })
    }

    const isStreet = place?.type === PlaceType.Street

    return t(`listings:SEO-header.meta-${selectedListingType || 'active'}-description-${isStreet ? 'on' : 'in'}`, {
      ...sharedVars,
      streetName: isStreet ? place.nameShort : '',
      placeNameShort:
        (isStreet
          ? [place?.city?.nameShort, place?.region?.nameAbbreviated].filter(Boolean).join(', ')
          : place?.nameShort) || t('listings:SEO-header.canada'),
      placeNameLong: place?.nameLong || t('listings:SEO-header.canada'),
      medianPrice: currency(
        selectedListingType === 'sold' ? comparablesData?.salePrice : comparablesData?.listingPrice
      ),
      count,
    })
  }, [
    comparablesData?.listingPrice,
    comparablesData?.salePrice,
    filterSelections.geo,
    place?.city?.nameShort,
    place?.nameLong,
    place?.nameShort,
    place?.region?.nameAbbreviated,
    place?.type,
    selectedListingType,
    selectedResidentialType,
    t,
    totalResults,
    count,
  ])

  const canonicalLink = useMemo(() => {
    let link = `${ORIGIN_HOST}/${DEFAULT_LISTING_DIRECTORY}/${query.listing}`

    if (query.page) {
      link += `?page=${query.page}`
    }

    return link
  }, [query.listing, query.page])

  return useMemo(() => {
    // Non-slugged routes return a default route-based title
    if (!query.listing) {
      return {
        breadcrumbItems,
        title: t(`listings:${pathname.replace(/\/real-estate\//g, '')}`, undefined, {
          fallback: t('listings:SEO-header.real-estate'),
        }),
      }
    }

    return {
      breadcrumbItems,
      title,
      metaDescription,
      canonicalLink,
    }
  }, [breadcrumbItems, canonicalLink, metaDescription, pathname, query.listing, t, title])
}
